import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../api/auth';
import { usersAPI } from '../api/users';
import { formatApiErrorMessage } from '../utils/api-error-message';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
  ThemedBottomActionBar,
} from '../components/ui/ThemedSurface';

type ChangeSecureForm = {
  old_answer: string;
  new_answer: string;
  transaction_password: string;
};

type SetupSecureForm = {
  transaction_password: string;
  security_question: string;
  security_answer: string;
};

const SECURITY_QUESTION_OPTIONS = [
  { key: 'secure.questionMother', textKey: 'secure.questionMother' },
  { key: 'secure.questionSpouseBirthday', textKey: 'secure.questionSpouseBirthday' },
] as const;

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.6 10.6A2 2 0 0 0 13.4 13.4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.9 5.2A9.4 9.4 0 0 1 12 5c5.5 0 9 7 9 7a15.2 15.2 0 0 1-2 2.8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.6 6.6C4.3 8.2 3 12 3 12s3.5 7 9 7a8.8 8.8 0 0 0 4.4-1.2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.1 12S5.7 5 12 5s9.9 7 9.9 7-3.6 7-9.9 7-9.9-7-9.9-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PasswordInput({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <ThemedInput
        type={visible ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="pr-11"
      />
      <button
        type="button"
        onClick={() => setVisible((show) => !show)}
        className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
        style={{ color: visible ? C.gold : C.muted }}
        aria-label={visible ? t('common.hidePassword') : t('common.showPassword')}
      >
        <PasswordVisibilityIcon visible={visible} />
      </button>
    </div>
  );
}

function SecureHero({ isSetup }: { isSetup: boolean }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div
          className="pointer-events-none absolute inset-x-4 top-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }}
        />
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xl font-semibold leading-none" style={{ color: C.text }}>
              {t('secure.title')}
            </div>
            <div className="mt-2 text-xs leading-relaxed" style={{ color: C.muted }}>
              {isSetup ? t('secure.setupHint') : t('secure.subtitle')}
            </div>
          </div>
          <div
            className="flex size-12 shrink-0 items-center justify-center rounded-2xl border"
            style={{
              borderColor: `${C.gold}66`,
              background: C.chipBg,
              color: C.gold,
              boxShadow: `0 0 20px ${C.gold}18`,
            }}
          >
            <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5c0 4.5-2.8 8.5-7 10-4.2-1.5-7-5.5-7-10V6l7-3Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 12l1.7 1.7L15.5 9" />
            </svg>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

function SecurityQuestionsPanel({ selectedQuestion, onSelect }: { selectedQuestion: string; onSelect: (q: string) => void }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-4">
      <div className="text-sm font-semibold" style={{ color: C.text }}>
        {t('secure.questionSetup')}
      </div>
      <div className="mt-2 text-xs leading-relaxed" style={{ color: C.muted }}>
        {t('secure.infoText')}
      </div>
      <div className="mt-4 flex flex-col gap-3">
        {SECURITY_QUESTION_OPTIONS.map((opt, index) => {
          const label = t(opt.textKey);
          const active = selectedQuestion === label;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onSelect(label)}
              className="flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition active:scale-[0.99]"
              style={{
                borderColor: active ? `${C.gold}88` : C.line,
                background: active ? `${C.gold}18` : C.inputBg,
              }}
            >
              <span
                className="flex size-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold"
                style={{
                  borderColor: `${C.gold}55`,
                  background: C.chipBg,
                  color: C.gold,
                }}
              >
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="text-[10px]" style={{ color: C.muted }}>
                  {t('secure.questionN', { n: index + 1 })}
                </div>
                <div className="mt-1 truncate text-sm font-semibold" style={{ color: active ? C.gold : C.text }}>
                  {label}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </ThemedPanel>
  );
}

function SetupSecureFormPanel({
  form,
  onPatch,
  error,
  success,
}: {
  form: SetupSecureForm;
  onPatch: (key: keyof SetupSecureForm, value: string) => void;
  error: string;
  success: string;
}) {
  const { t } = useTranslation();

  return (
    <>
      <SecurityQuestionsPanel
        selectedQuestion={form.security_question}
        onSelect={(q) => onPatch('security_question', q)}
      />
      <ThemedPanel className="p-4">
        <div className="mb-4 text-sm font-semibold" style={{ color: C.text }}>
          {t('secure.setupTitle')}
        </div>
        <div className="flex flex-col gap-5">
          <ThemedField label={t('common.txPassword')} required>
            <PasswordInput
              value={form.transaction_password}
              placeholder={t('common.enterTxPassword')}
              onChange={(value) => onPatch('transaction_password', value)}
            />
            <p className="mt-1 text-[10px]" style={{ color: C.muted }}>
              {t('secure.txPasswordHint')}
            </p>
          </ThemedField>

          <ThemedField label={t('secure.answer')} required>
            <ThemedInput
              type="text"
              placeholder={t('secure.answerPlaceholder')}
              value={form.security_answer}
              onChange={(e) => onPatch('security_answer', e.target.value)}
            />
          </ThemedField>

          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
          {success && <ThemedNotice tone="success">{success}</ThemedNotice>}
        </div>
      </ThemedPanel>
    </>
  );
}

function ChangeSecureFormPanel({
  form,
  onPatch,
  securityQuestion,
  error,
  success,
}: {
  form: ChangeSecureForm;
  onPatch: (key: keyof ChangeSecureForm, value: string) => void;
  securityQuestion: string | null | undefined;
  error: string;
  success: string;
}) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-4">
      <div className="mb-3 rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, color: C.muted }}>
        {t('common.securityQuestion')}：
        <span className="font-semibold" style={{ color: C.gold }}>
          {securityQuestion || t('common.defaultSecurityQuestion')}
        </span>
      </div>
      <div className="mb-4 text-sm font-semibold" style={{ color: C.text }}>
        {t('secure.changeSecure')}
      </div>
      <div className="flex flex-col gap-5">
        <ThemedField label={t('secure.oldAnswer')} required>
          <ThemedInput
            type="text"
            placeholder={t('secure.oldAnswerPlaceholder')}
            value={form.old_answer}
            onChange={(e) => onPatch('old_answer', e.target.value)}
          />
        </ThemedField>

        <ThemedField label={t('secure.newAnswer')} required>
          <ThemedInput
            type="text"
            placeholder={t('secure.newAnswerPlaceholder')}
            value={form.new_answer}
            onChange={(e) => onPatch('new_answer', e.target.value)}
          />
        </ThemedField>

        <ThemedField label={t('common.txPassword')} required>
          <PasswordInput
            value={form.transaction_password}
            placeholder={t('common.enterTxPassword')}
            onChange={(value) => onPatch('transaction_password', value)}
          />
        </ThemedField>

        {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
        {success && <ThemedNotice tone="success">{success}</ThemedNotice>}
      </div>
    </ThemedPanel>
  );
}

export function SecurePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const statusQuery = useQuery({
    queryKey: ['users', 'security-status'],
    queryFn: () => usersAPI.getSecurityStatus(),
  });

  const isSetup = statusQuery.data ? !statusQuery.data.transaction_password_set : false;

  const defaultQuestion = t('secure.questionMother');
  const [setupForm, setSetupForm] = useState<SetupSecureForm>({
    transaction_password: '',
    security_question: defaultQuestion,
    security_answer: '',
  });
  const [changeForm, setChangeForm] = useState<ChangeSecureForm>({
    old_answer: '',
    new_answer: '',
    transaction_password: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const setupDisabled =
    submitting ||
    !setupForm.transaction_password.trim() ||
    setupForm.transaction_password.length < 6 ||
    !setupForm.security_question.trim() ||
    !setupForm.security_answer.trim();

  const changeDisabled =
    submitting ||
    !changeForm.old_answer.trim() ||
    !changeForm.new_answer.trim() ||
    !changeForm.transaction_password.trim();

  function patchSetup(key: keyof SetupSecureForm, value: string) {
    setSetupForm((prev) => ({ ...prev, [key]: value }));
  }

  function patchChange(key: keyof ChangeSecureForm, value: string) {
    setChangeForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSetupSubmit() {
    setError('');
    setSuccess('');
    if (setupForm.transaction_password.length < 6) {
      setError(t('secure.txPasswordHint'));
      return;
    }
    if (!setupForm.security_question.trim() || !setupForm.security_answer.trim()) {
      setError(t('secure.fillRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await usersAPI.setupSecurity({
        transaction_password: setupForm.transaction_password,
        security_question: setupForm.security_question,
        security_answer: setupForm.security_answer,
      });
      setSuccess(t('secure.setupSuccessMsg'));
      void qc.invalidateQueries({ queryKey: ['users', 'security-status'] });
      void qc.invalidateQueries({ queryKey: ['profile'] });
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, t('common.error')));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleChangeSubmit() {
    setError('');
    setSuccess('');
    if (!changeForm.old_answer.trim() || !changeForm.new_answer.trim() || !changeForm.transaction_password.trim()) {
      setError(t('secure.fillRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.changeSecurityAnswer(changeForm);
      setSuccess(t('secure.successMsg'));
      setChangeForm({ old_answer: '', new_answer: '', transaction_password: '' });
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, t('common.error')));
    } finally {
      setSubmitting(false);
    }
  }

  const loading = statusQuery.isLoading;

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('secure.title')} onBack={() => navigate(-1)} theme={C} />
        <div
          className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]"
          style={{ height: 'calc(100% - 70px)', paddingBottom: '12px' }}
        >
          {loading ? (
            <ThemedPanel className="p-4 text-center text-sm" style={{ color: C.muted }}>
              {t('common.loading')}
            </ThemedPanel>
          ) : (
            <>
              <SecureHero isSetup={isSetup} />
              {isSetup ? (
                <SetupSecureFormPanel form={setupForm} onPatch={patchSetup} error={error} success={success} />
              ) : (
                <ChangeSecureFormPanel
                  form={changeForm}
                  onPatch={patchChange}
                  securityQuestion={statusQuery.data?.security_question}
                  error={error}
                  success={success}
                />
              )}
            </>
          )}
        </div>

        {!loading && (
          <ThemedBottomActionBar>
            <ThemedSubmitButton
              disabled={isSetup ? setupDisabled : changeDisabled}
              onClick={() => void (isSetup ? handleSetupSubmit() : handleChangeSubmit())}
            >
              {submitting ? '...' : isSetup ? t('secure.setupSubmit') : t('secure.submit')}
            </ThemedSubmitButton>
          </ThemedBottomActionBar>
        )}
      </div>
    </div>
  );
}

export default SecurePage;
